import { useState, useEffect, useRef } from 'react';
import { gsap, ScrollTrigger } from '@/hooks/useGsapAnimations';

gsap.registerPlugin(ScrollTrigger);

const FAQ = () => {
    const [openIndex, setOpenIndex] = useState<number | null>(0);
    const sectionRef = useRef<HTMLElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const faqs = [
        {
            question: "How does the question scanning feature work?",
            answer: "Simply take a photo of any question using your device's camera. Our advanced AI analyzes the image, recognizes the text and mathematical equations, and provides detailed step-by-step solutions within seconds."
        },
        {
            question: "Can I use this app for all subjects?",
            answer: "Yes! Eddy AI supports a wide range of subjects including mathematics, physics, chemistry, biology, and more. Our AI is trained on diverse academic content to help you with various topics across different disciplines."
        },
        {
            question: "How are the quizzes personalized?",
            answer: "Our AI analyzes your learning patterns, strengths, and areas for improvement to generate customized quizzes. The difficulty level and question types adapt based on your performance, ensuring an optimal learning experience."
        },
        {
            question: "Is the app free to use?",
            answer: "Eddy AI is completely free to use at the moment. Users get full access to essential features including question solving, quizzes, and quick notes generation, all designed to make learning faster and easier."
        },
        {
            question: "Can I save my generated notes and solved questions?",
            answer: "Yes, your notes, answers, and quizzes are automatically saved. You can also mark important ones as favourites for quick access later."
        }
    ];

    const toggleFAQ = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return;

        const ctx = gsap.context(() => {
            // Header fade in
            if (headerRef.current) {
                gsap.fromTo(
                    headerRef.current,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.8,
                        ease: 'power2.out',
                        scrollTrigger: {
                            trigger: headerRef.current,
                            start: 'top 85%',
                            toggleActions: 'play none none none',
                        },
                    }
                );
            }

            // FAQ items stagger in from bottom
            if (listRef.current) {
                const items = listRef.current.querySelectorAll('.faq-item');
                gsap.fromTo(
                    items,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.6,
                        ease: 'power2.out',
                        stagger: 0.12,
                        scrollTrigger: {
                            trigger: listRef.current,
                            start: 'top 85%',
                            toggleActions: 'play none none none',
                        },
                    }
                );
            }
        }, el);

        return () => ctx.revert();
    }, []);

    return (
        <section ref={sectionRef} id="faq" className="relative z-10 px-4 sm:px-6 lg:px-20 py-20 md:py-28 max-w-[1200px] mx-auto">
            {/* Section Header */}
            <div ref={headerRef} className="text-center mb-12 sm:mb-16 space-y-3 sm:space-y-4" style={{ opacity: 0 }}>
                <h2
                    className="text-white"
                    style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 500,
                        fontSize: "clamp(28px, 6vw, 48px)",
                        lineHeight: "1.3",
                        letterSpacing: "0px",
                    }}
                >
                    Frequently Asked Questions
                </h2>
                <p
                    className="text-[#DCCBFD]"
                    style={{
                        fontFamily: "'Roboto', sans-serif",
                        fontWeight: 400,
                        fontSize: "clamp(14px, 3.5vw, 16px)",
                        lineHeight: "clamp(22px, 5vw, 26px)",
                        letterSpacing: "0.3px",
                    }}
                >
                    Everything you need to know about Eddy AI
                </p>
            </div>

            {/* FAQ Accordion */}
            <div ref={listRef} className="space-y-4 sm:space-y-5 bg-gradient-to-b rounded-[16px] from-[#8E57FA]/5 to-[#8E57FA]/15">
                {faqs.map((faq, index) => (
                    <div
                        key={index}
                        className={`faq-item rounded-[16px]  backdrop-blur-sm transition-all duration-300 hover:scale-[1.01] hover:border-[#8E57FA]/50 ${openIndex === index
                            ? 'bg-[#8E57FA]/50 shadow-[0_80px_40px_rgba(142, 87, 250, 0.2)] scale-[1.01]'
                            : 'bg-transparent hover:bg-gradient-to-b hover:from-[#8E57FA]/30 hover:to-[#8E57FA]/10 hover:shadow-[0_20px_40px_rgba(142, 87, 250, 0.2)]'
                            }`}
                        style={{ opacity: 0 }}
                    >
                        {/* Question Button */}
                        <button
                            onClick={() => toggleFAQ(index)}
                            className="w-full px-6 sm:px-8 py-5 sm:py-6 flex items-center justify-between gap-4 text-left transition-colors duration-200"
                        >
                            <span
                                className={`text-white flex-1 transition-colors duration-300 ${openIndex === index ? 'text-white' : 'text-white'}`}
                                style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontWeight: 500,
                                    fontSize: "clamp(15px, 3.5vw, 18px)",
                                    lineHeight: "1.5",
                                }}
                            >
                                {faq.question}
                            </span>

                            {/* Toggle Icon */}
                            <div
                                className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-[14px] flex items-center justify-center transition-all duration-300 ${openIndex === index
                                    ? 'bg-[#321E58] rotate-180'
                                    : 'bg-[#8E57FA] group-hover:bg-white/20'
                                    }`}
                            >
                                <svg
                                    className="w-5 h-5 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 9l-7 7-7-7"
                                    />
                                </svg>
                            </div>
                        </button>

                        {/* Answer Panel */}
                        <div
                            className={`overflow-hidden transition-all duration-300 ease-in-out ${openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                                }`}
                        >
                            <div className="px-6 sm:px-8 pb-5 sm:pb-6 pt-2">
                                <p
                                    className="text-[#DCCBFD] group-hover:text-white/90 transition-colors duration-300"
                                    style={{
                                        fontFamily: "'Roboto', sans-serif",
                                        fontWeight: 400,
                                        fontSize: "clamp(13px, 3vw, 15px)",
                                        lineHeight: "clamp(22px, 5vw, 26px)",
                                        letterSpacing: "0.3px",
                                    }}
                                >
                                    {faq.answer}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>


        </section>
    );
};

export default FAQ;
